'use client'

import { useWallet } from '@/providers/WalletContext'
import { useQuery } from '@tanstack/react-query'
import type { Address, PublicClient } from 'viem'

export interface UseDestinationDeployedResult {
	// undefined while the check is in flight or address is missing,
	// true if the destination address has bytecode on-chain (i.e. is a deployed
	// contract), false if the address is empty (EOA / predicted CREATE2 not yet
	// deployed). Deposit flows that route into a contract MUST gate on `true`
	// before allowing the user to send funds — otherwise the deposit can land
	// on an undeployed predicted address and remain stuck until someone deploys
	// the contract there.
	isDeployed: boolean | undefined
	isChecking: boolean
}

export interface UseDestinationDeployedOptions {
	publicClientOverride?: PublicClient | null
	enabled?: boolean
}

export function useDestinationDeployed(
	address: string | undefined,
	options: UseDestinationDeployedOptions = {}
): UseDestinationDeployedResult {
	const wallet = useWallet()
	const publicClient = options.publicClientOverride ?? wallet.publicClient
	const enabled = (options.enabled ?? true) && !!address && !!publicClient

	const query = useQuery<boolean>({
		queryKey: ['destination-deployed', address?.toLowerCase()] as const,
		queryFn: async () => {
			if (!(address && publicClient)) return false
			const code = await publicClient.getCode({ address: address as Address })
			return code !== undefined && code !== '0x'
		},
		enabled,
		// Bytecode at a CREATE2 address is monotonic, but the user may deploy
		// via another flow (trading init) mid-session. 30s is a cheap recheck
		// cadence — one eth_getCode call per visit.
		staleTime: 30_000,
		retry: 1,
	})

	return {
		isDeployed: query.data,
		isChecking: query.isFetching,
	}
}
